import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Divider,
    CircularProgress,
    IconButton,
    LinearProgress,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    SelectChangeEvent
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { getZoomCompensatedSelectMenuProps } from '../../../utils/zoomCompensation';
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
    Legend
} from 'recharts';
import { projectsService } from '../../../services/projectsService';
import { Project } from '../../../types';
import { useAuth } from '../../../contexts/AuthContext';
import { hasCapability, CAP } from '../../../utils/permissions';
import { isUserOnProjectTeam } from '../../../utils/projectTeam';

interface KPIData {
    documentStatuses: { name: string; value: number; color: string }[];
    activeProjects: number;
    pendingActions: number;
    overdueItems: number;
    documentStatusSummary: {
        approved: number;
        pending: number;
        cancelled: number;
    };
    plannedVsActual: {
        plannedProgress: number;
        actualProgress: number;
        plannedDocuments: number;
        actualDocuments: number;
        totalDocuments: number;
    };
}

const KPIWidget: React.FC = () => {
    const { user } = useAuth();
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProjectId, setSelectedProjectId] = useState<string>('all');
    const [kpiData, setKpiData] = useState<KPIData | null>(null);
    const [activeKpiView, setActiveKpiView] = useState<0 | 1>(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Fetch projects for the dropdown - filtered by user membership
    useEffect(() => {
        const fetchProjects = async () => {
            if (!user) return;

            try {
                const allProjects = await projectsService.getProjects();

                // PM/Admin/Director users see all projects, others only see projects they're PM or engineer of
                const canAccessAllProjects = hasCapability(user, CAP.VIEW_ALL_PROJECTS);
                if (canAccessAllProjects) {
                    setProjects(allProjects);
                } else {
                    const userProjects = allProjects.filter(project => isUserOnProjectTeam(project, user));
                    setProjects(userProjects);
                }
            } catch (err) {
                console.error('Failed to fetch projects', err);
            }
        };
        fetchProjects();
    }, [user]);

    // Fetch KPI data (initially and when project changes)
    useEffect(() => {
        const fetchKPIData = async () => {
            setLoading(true);
            setError(null);
            try {
                const projectId = selectedProjectId === 'all' ? undefined : parseInt(selectedProjectId);
                const data = await projectsService.getDashboardKPI(projectId);
                setKpiData(data);
            } catch (err) {
                console.error('Failed to fetch KPI data', err);
                setError('Failed to load KPI data');
            } finally {
                setLoading(false);
            }
        };
        fetchKPIData();
    }, [selectedProjectId]);

    const handleProjectChange = (event: SelectChangeEvent) => {
        setSelectedProjectId(event.target.value);
    };
    const handlePreviousKpi = () => {
        setActiveKpiView((prev) => (prev === 0 ? 1 : 0));
    };
    const handleNextKpi = () => {
        setActiveKpiView((prev) => (prev === 1 ? 0 : 1));
    };

    if (error) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const chartData = kpiData?.documentStatuses || [];
    const hasData = chartData.some(d => d.value > 0);

    return (
        <Box sx={{ height: '360px', width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <IconButton size="small" onClick={handlePreviousKpi} aria-label="Previous KPI">
                    <ChevronLeft fontSize="small" />
                </IconButton>
                <Typography variant="caption" color="text.secondary">
                    {activeKpiView === 0 ? 'Document Status KPIs' : 'Planned vs Actual Progress'}
                </Typography>
                <IconButton size="small" onClick={handleNextKpi} aria-label="Next KPI">
                    <ChevronRight fontSize="small" />
                </IconButton>
            </Box>

            {/* Project Filter */}
            <FormControl size="small" sx={{ mt: 0.5, mb: 1, minWidth: 150 }}>
                <InputLabel id="kpi-project-select-label">Project</InputLabel>
                <Select
                    labelId="kpi-project-select-label"
                    id="kpi-project-select"
                    value={selectedProjectId}
                    label="Project"
                    onChange={handleProjectChange}
                    MenuProps={getZoomCompensatedSelectMenuProps({
                        disablePortal: false,
                        sx: { zIndex: 1400 }
                    })}
                >
                    <MenuItem value="all">All Projects</MenuItem>
                    {projects.map((project) => (
                        <MenuItem key={project.id} value={project.id.toString()}>
                            {project.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                    <CircularProgress size={32} />
                </Box>
            ) : (
                <>
                    {activeKpiView === 0 ? (
                        <>
                            {/* Pie Chart */}
                            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                                {hasData ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={chartData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={50}
                                                outerRadius={70}
                                                paddingAngle={5}
                                                dataKey="value"
                                            >
                                                {chartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            <Legend verticalAlign="bottom" height={36} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                        <Typography color="text.secondary">No document revisions yet</Typography>
                                    </Box>
                                )}
                            </Box>

                            <Divider sx={{ my: 1 }} />

                            {/* KPI Metrics */}
                            <Grid container spacing={2} sx={{ textAlign: 'center' }}>
                                <Grid item xs={4}>
                                    <Typography variant="h4" color="success.main">
                                        {kpiData?.documentStatusSummary?.approved ?? 0}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">Approved</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="h4" color="warning.main">
                                        {kpiData?.documentStatusSummary?.pending ?? 0}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">Pending</Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="h4" color="error.main">
                                        {kpiData?.documentStatusSummary?.cancelled ?? 0}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">Cancelled</Typography>
                                </Grid>
                            </Grid>
                        </>
                    ) : (
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary">Planned Progress</Typography>
                                    <Typography variant="body2" color="warning.main">
                                        {kpiData?.plannedVsActual?.plannedProgress ?? 0}%
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(100, Math.max(0, kpiData?.plannedVsActual?.plannedProgress ?? 0))}
                                    color="warning"
                                />
                                <Typography variant="caption" color="text.secondary">
                                    {kpiData?.plannedVsActual?.plannedDocuments ?? 0} / {kpiData?.plannedVsActual?.totalDocuments ?? 0} revisions due
                                </Typography>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2" color="text.secondary">Actual Progress</Typography>
                                    <Typography variant="body2" color="success.main">
                                        {kpiData?.plannedVsActual?.actualProgress ?? 0}%
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(100, Math.max(0, kpiData?.plannedVsActual?.actualProgress ?? 0))}
                                    color="success"
                                />
                                <Typography variant="caption" color="text.secondary">
                                    {kpiData?.plannedVsActual?.actualDocuments ?? 0} / {kpiData?.plannedVsActual?.totalDocuments ?? 0} revisions approved
                                </Typography>
                            </Box>

                            <Divider sx={{ my: 1 }} />

                            <Grid container spacing={2} sx={{ textAlign: 'center' }}>
                                <Grid item xs={6}>
                                    <Typography variant="h5" color="warning.main">
                                        {kpiData?.plannedVsActual?.plannedProgress ?? 0}%
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">Planned</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="h5" color="success.main">
                                        {kpiData?.plannedVsActual?.actualProgress ?? 0}%
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">Actual</Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default KPIWidget;
